<template>
    <div>
        <CardSelect
            v-model="person.addressType"
            type="radio"
            :required="required || isOrganisation"
            :name="$t('eventRegistration.person.addressType')"
            :fixed-width="false">
            <CardSelectItem
                v-for="addressType in addressTypes?.filter(type => (onlyPersonsAsParticipants && !isOrganisation && type.label.toLowerCase().includes('privat')) || !onlyPersonsAsParticipants)"
                :key="addressType.id"
                :disabled="isOrganisation"
                :value="addressType.id"
                :icon="addressType.label.toLowerCase().includes('privat') ? 'user' : 'industry'">
                {{ addressType.label }}
            </CardSelectItem>
        </CardSelect>

        <BaseSelect
            v-if="availablePersonsForAddressApply && availablePersonsForAddressApply.length !== 0"
            v-model="person.applyAddressFromParticipant"
            autocomplete="off"
            :label="$t('eventRegistration.person.applyAddressFromParticipant')"
            clearable
            :no-data-text="$t('eventRegistration.person.noCitiesFound')"
            :placeholder="$t('eventRegistration.person.applyAddressFromParticipantSelectPlaceholder')">
            <BaseOption
                v-for="(person, key) in availablePersonsForAddressApplyWithoutLength"
                :key="key"
                :label="getPersonString(person)"
                :value="key" />
        </BaseSelect>

        <BaseInput
            v-if="isAddressTypeBusiness && !isOrganisation"
            v-model="person.toTheAttention"
            :label="$t('eventRegistration.person.organisationName')"
            :required="required"
            :rules="{max: 100}" />

        <BaseSelect
            v-model="person.countryId"
            :label="$t('eventRegistration.person.country')"
            :required="required"
            autocomplete="off"
            :placeholder="$t('eventRegistration.person.country')"
            :clearable="!required"
            :filterable="!isMobile">
            <BaseOption
                v-for="country in countries"
                :key="country.id"
                :value="country.id"
                :label="country.label" />
        </BaseSelect>

        <RegistrationAddressSelect
            v-if="person.countryId === 'DEU'"
            :person-key="personKey"
            :required="required"
            :disable-address-watchers="disableAddressWatchers" />

        <RegistrationAddressInput
            v-else
            :person-key="personKey"
            :required="required" />
    </div>
</template>

<script>
import registrationPersonMixin from "@/mixins/registration/registrationPersonMixin";
import formSelectChoiceMixin from "@/mixins/registration/formSelectChoiceMixin";
import RegistrationAddressSelect from "@/components/pages/registration/address/RegistrationAddressSelect";
import RegistrationAddressInput from "@/components/pages/registration/address/RegistrationAddressInput";
import detectMobileMixin from "@/mixins/detectMobileMixin";

export default {
    name: "RegistrationAddressFormMain",
    components: {
        RegistrationAddressInput,
        RegistrationAddressSelect
    },
    mixins: [formSelectChoiceMixin, registrationPersonMixin, detectMobileMixin],
    props: {
        required: {
            type: Boolean,
            default: false
        },
        isOrganisation: {
            type: Boolean,
            default: false
        },
        onlyPersonsAsParticipants: {
            type: [Boolean, Number],
            default: false
        }

    },
    data() {
        return {
            disableAddressWatchers: false
        }
    },
    computed: {
        isAddressTypeBusiness() {
            const currentAddressType = this.addressTypes.find(addressType => addressType.id === this.person.addressType)
            return currentAddressType && currentAddressType.label.toLowerCase().includes('geschäft')
        },
        selectableAddressTypes() {
            return this.addressTypes?.filter(addressType => (this.onlyPersonsAsParticipants && !this.isOrganisation && addressType.label.toLowerCase().includes('privat')) || !this.onlyPersonsAsParticipants) ?? []
        },
        availablePersonsForAddressApplyWithoutLength() {
            const availablePersonsForAddressApply = this.$cloneDeep(this.availablePersonsForAddressApply)
            delete availablePersonsForAddressApply.length
            return availablePersonsForAddressApply
        }
    },
    methods: {
        getPersonString(person) {
            return person.zipValue + ' ' + person.cityValue + ' ' + person.streetValue + ' ' + person.houseNumber + ' ' + (person.toTheAttention ?? '') + ' ' + (person.additional1 ?? '')
        }
    },
    mounted() {
        if (!this.person.countryId) {
            this.$nextTick(() => {
                this.person.countryId = 'DEU'
            })
        }
    },
    watch: {
        'person.countryId': {
            handler() {
                if (this.disableAddressWatchers) return
                this.disableAddressWatchers = true
                const newPersonData = this.$cloneDeep(this.person)
                if (newPersonData.zipValue) newPersonData.zipValue = null
                if (newPersonData.cityId) newPersonData.cityId = null
                if (newPersonData.cityValue) newPersonData.cityValue = null
                if (newPersonData.streetId) newPersonData.streetId = null
                if (newPersonData.streetValue) newPersonData.streetValue = null
                if (newPersonData.houseNumber) newPersonData.houseNumber = null
                if (newPersonData.isPostbox) newPersonData.isPostbox = null
                if (newPersonData.postboxNumber) newPersonData.postboxNumber = null

                this.person = this.$cloneDeep(newPersonData)
                this.$nextTick(() => {
                    this.disableAddressWatchers = false
                })
            }
        },
        'person.applyAddressFromParticipant': {
            handler() {
                this.disableAddressWatchers = true
                const person = this.registrationData.persons[this.person.applyAddressFromParticipant]

                this.person.addressType = person?.addressType ?? null
                this.person.toTheAttention = person?.toTheAttention ?? null
                this.person.zipValue = person?.zipValue ?? null
                this.person.cityId = person?.cityId ?? null
                this.person.cityValue = person?.cityValue ?? null
                this.person.houseNumber = person?.houseNumber ?? null
                this.person.streetId = person?.streetId ?? null
                this.person.streetValue = person?.streetValue ?? null
                this.person.isPostbox = person?.isPostbox ?? null
                this.person.postboxNumber = person?.postboxNumber ?? null
                this.person.additional1 = person?.additional1 ?? null
                this.person.countryId = person?.countryId ?? 'DEU'

                if (this.isOrganisation) {
                    const businessAddressType = this.addressTypes.find(addressType => addressType.label.toLowerCase().includes('geschäft'))
                    if (businessAddressType) this.person.addressType = businessAddressType.id
                }

                this.$nextTick(() => {
                    this.disableAddressWatchers = false
                })
            }
        },
        isOrganisation: {
            handler(isOrganisation) {
                if (isOrganisation) {
                    const businessAddressType = this.addressTypes.find(addressType => addressType.label.toLowerCase().includes('geschäft'))
                    if (businessAddressType)
                        this.person.addressType = businessAddressType.id
                }
            },
            immediate: true
        }
    }
}
</script>

<style scoped>

</style>
